import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { environment } from '@env/environment';
import { APPLICATION_STATUS, APPLICATION_TYPES, LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { IAtp } from 'app/models/atp/atp';
import { IAtpApplication } from 'app/models/atp/atpApplication';
import { AtpService } from 'app/services/atp/atp.service';
import { CommonDataService } from 'app/services/common/common-data.service';
import { SharedService } from 'app/services/core/shared.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { AddAtpApplicationComponent } from './add-atp-application/add-atp-application.component';
import { AtpExampleDocumentsManagerComponent } from './atp-example-documents-manager/atp-example-documents-manager.component';

@Component({
  selector: 'app-atp-application-list',
  templateUrl: './atp-application-list.component.html',
  styleUrls: ['./atp-application-list.component.scss']
})
export class AtpApplicationListComponent implements OnInit, OnChanges {
  @Input() atpId: number | null = null; // If provided, shows applications for specific ATP
  @Input() atp: IAtp | null = null; // Full ATP object if available from parent
  @Input() atpStaff: any[] | null = null; // Staff members from parent ATP
  @Input() targetContext: any | null = null;
  @Input() isEmbedded: boolean = false; // If true, hides header and some filters
  @Output() countChanged = new EventEmitter<number>(); // Emits the filtered application count
  
  public environment = environment;
  public isMobile: boolean = false;
  public appStatus = APPLICATION_STATUS;
  public appTypes = APPLICATION_TYPES;

  public filteredApplications = [];  
  public filterString: string = '';
  public selectedStatus = new FormControl([]);
  public selectedType = new FormControl([]);
  public showPendingLinkApprovals = new FormControl(true);
  public applicationDateFilterStart = new FormControl("");
  public applicationDateFilterEnd = new FormControl("");
  public selectedRenewal = new FormControl("");
  public dataSource: MatTableDataSource<any>;
  public pendingLinkApprovalApplicationIds = new Set<number>();

  @ViewChild('TablePaginator') paginator: MatPaginator;
  @ViewChild('TableSort') sort: MatSort;
  
  loading: boolean = true;
  pageSize: number = 50;  
  uiData = LEAD_BASED_PAINT;  
  atpApplicationTable = {
    icon: 'description',
    title:'Accredited Training Provider Applications',
    layout:{
      columns:['id','atpName', 'applicationStatus', 'applicationFeedback', 'applicationType', 'applicationDate', 'approvalDate'],
      container:[
        {displayName:'Application Id',columnName:'id', type:'string', size:'12'},
        {displayName:'Application Type',columnName:'applicationType', type:'string', size:'15'},
        {displayName:'ATP Name',columnName:'atpName', type:'string', size:'15'},
        {displayName:'Status',columnName:'applicationStatus', type:'status', size:'10'},
        {displayName:'Application Feedback',columnName:'applicationFeedback', type:'string', size:'15'},
        {displayName:'Application Date',columnName:'applicationDate', type:'date', size:'12'},
        {displayName:'Approval Date',columnName:'approvalDate', type:'date', size:'12'},          
      ],
      data: []      
    }
  }

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    public sharedService: SharedService,
    public atpService: AtpService,
    public commonService: CommonDataService,
    public toastr: ToastrService,
    public dialog: MatDialog,
    ) 
    {
      this.breakpointObserver.observe([
        Breakpoints.Handset,
        Breakpoints.Tablet,
        Breakpoints.Small,
      ]).subscribe(result => {
        this.isMobile = result.matches;
      });
    }

  statusColor(status: string): string {
    return this.sharedService.getStatusColor(status);
  }

  ngOnInit(): void {
    // Only load data on init if we have an atpId (embedded mode) or not embedded (standalone mode)
    if (this.atpId || !this.isEmbedded) {
      this.loadData();
    }
  }

  openExampleDocumentsManager(): void {
    this.dialog.open(AtpExampleDocumentsManagerComponent, {
      width: '900px',
      maxWidth: '95vw',
      autoFocus: false
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When atpId changes and we're embedded, reload the data
    if (changes['atpId'] && !changes['atpId'].firstChange && this.atpId) {
      this.loadData();
    }
  }

  public loadData(){
    // If embedded, we must have an atpId
    if (this.isEmbedded && !this.atpId) {
      console.warn('AtpApplicationList: Embedded mode requires atpId');
      this.loading = false;
      return;
    }
    
    // Only load/save filters if not embedded (standalone page view)
    if (!this.isEmbedded) {
      this.filterString = this.commonService.atpApplicationFilter.filterString;
      this.selectedStatus = this.commonService.atpApplicationFilter.selectedStatus;
      this.selectedType = this.commonService.atpApplicationFilter.selectedType;
      this.showPendingLinkApprovals = this.commonService.atpApplicationFilter.showPendingLinkApprovals;
      this.applicationDateFilterStart.setValue(this.commonService.atpApplicationFilter.applicationDateFilterStart === null ? null : new Date(this.commonService.atpApplicationFilter.applicationDateFilterStart).toISOString().substring(0,10));
      this.applicationDateFilterEnd.setValue(this.commonService.atpApplicationFilter.applicationDateFilterEnd === null ? null : new Date(this.commonService.atpApplicationFilter.applicationDateFilterEnd).toISOString().substring(0,10));
      this.selectedRenewal = this.commonService.atpApplicationFilter.selectedRenewal;
    }    
    
    // Choose which service method to call based on whether atpId is provided
    // - Embedded mode: show applications for the specific ATP
    // - Standalone mode: show all applications (admin view)
    const serviceCall = this.atpId
      ? this.atpService.getAllAtpAccreditationApplications(this.atpId)
      : this.atpService.getAllAtpApplications();

    forkJoin({
      applications: serviceCall,
      pendingLinkApprovalApplicationIds: this.commonService.getPendingLinkApprovalApplicationIds('ATP')
    }).subscribe(
      res=>{
        this.atpApplicationTable.layout.data = res.applications;
        this.filteredApplications = res.applications;
        this.pendingLinkApprovalApplicationIds = new Set(res.pendingLinkApprovalApplicationIds);
        this.loading = false;
        // Wait for the *ngIf to render the table before assigning sort/paginator
        setTimeout(() => {
          this.filterTable();
        });
      },
      err=>{this.toastr.error("An error occured loading applications: ", err); this.loading = false;}
    )
  }

  public getAllApplications(): any[] {
    return this.atpApplicationTable?.layout?.data ?? [];
  }

  updateDataSource(){
    this.dataSource = new MatTableDataSource<any>(this.filteredApplications);
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    
    // Emit the count for parent component to display
    this.countChanged.emit(this.filteredApplications.length);
  }

  private getApplicationContext(application: IAtpApplication | null): { atpId: number | null; atp: IAtp | null } {
    const applicationAtp = (application as any)?.atp || null;

    return {
      atpId: this.atpId || this.atp?.id || (application as any)?.atpId || applicationAtp?.id || null,
      atp: this.atp || applicationAtp || null
    };
  }

  openApplication(application: IAtpApplication){
    const applicationContext = this.getApplicationContext(application);

    const dialogRef = this.dialog.open(AddAtpApplicationComponent, this.sharedService.getWizardDialogConfig({
      data: {
        atpApplicationId: application ? application.id : null,
        atpId: applicationContext.atpId,
        atp: applicationContext.atp,
        atpStaff: this.atpStaff,
        targetContext: this.targetContext
      }
    }));
    dialogRef.afterClosed().subscribe(result => {
      if(result){
        this.loadData();        
      }        
    });    
  }

  private toDateOnly(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  isApprovedApplication(row: any): boolean {
    return (row?.applicationStatus || '').toString().trim().toLowerCase() === 'approved';
  }

  isAccountLinkApplication(row: any): boolean {
    const rawType = (row?.applicationType || '').toString().trim().toLowerCase();
    const displayType = this.getApplicationTypeDisplay(row?.applicationType).toString().trim().toLowerCase();

    return rawType === 'link' || displayType === APPLICATION_TYPES.link.toLowerCase();
  }

  isApplicationExpired(row: any): boolean {
    if (!row?.targetAccreditationExpirationDate) {
      return true;
    }

    const expirationDate = this.toDateOnly(new Date(row.targetAccreditationExpirationDate));
    const today = this.toDateOnly(new Date());
    return expirationDate < today;
  }

  isApplicationAmmendmentEligible(row: any): boolean {
    if (!this.isApprovedApplication(row) || !row?.targetAccreditationExpirationDate) {
      return false;
    }

    const expirationDate = this.toDateOnly(new Date(row.targetAccreditationExpirationDate));
    const sixMonthsFromNow = this.toDateOnly(new Date());
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
    return expirationDate > sixMonthsFromNow;
  }

  openAmmendmentApplication(application: IAtpApplication): void {
    if (!application?.id) {
      return;
    }

    const applicationContext = this.getApplicationContext(application);

    const dialogRef = this.dialog.open(AddAtpApplicationComponent, this.sharedService.getWizardDialogConfig({
      data: {
        atpApplicationId: null,
        atpId: applicationContext.atpId,
        atp: applicationContext.atp,
        atpStaff: this.atpStaff,
        sourceApplicationId: application.id,
        targetContext: this.targetContext
      }
    }));

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadData();
      }
    });
  }

  getStatusTypeValues() {    
    return Object.values(APPLICATION_STATUS);
  }
  
  getApplicationTypeValues() {
    return Object.keys(APPLICATION_TYPES);
  }

  getApplicationTypeDisplay(key: string): string {
    return APPLICATION_TYPES[key] || key;
  }

  getApplicationTypeWithCourses(row: any): string {
    const baseType = this.getApplicationTypeDisplay(row.applicationType);
    
    // Don't show courseData for Application Link or Link types
    if (row.applicationType === 'Application Link' || row.applicationType === 'Link') {
      return baseType;
    }
    
    if (row.courseData && Array.isArray(row.courseData) && row.courseData.length > 0) {
      // Filter out entries that start with '-' after trimming
      const filteredCourses = row.courseData
        .filter(course => !course.trim().startsWith('-'))
        .map(course => this.capitalizeWords(course));
      
      if (filteredCourses.length > 0) {
        const courses = filteredCourses.join('\n');
        return `${baseType}\n${courses}`;
      }
    }
    return baseType;
  }

  capitalizeWords(text: string): string {
    return text.split('-').map(word => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join('-');
  }

  getApplicationFilterValues() {
    return [null];
  }

  filterTable() {
    // Only save filters if not embedded
    if (!this.isEmbedded) {
      this.commonService.atpApplicationFilter.selectedStatus = this.selectedStatus;
      this.commonService.atpApplicationFilter.selectedType = this.selectedType;
      this.commonService.atpApplicationFilter.showPendingLinkApprovals = this.showPendingLinkApprovals;
      this.commonService.atpApplicationFilter.filterString = this.filterString;    
      this.commonService.atpApplicationFilter.applicationDateFilterStart = this.applicationDateFilterStart.value === null ? null : new Date(this.applicationDateFilterStart.value);    
      this.commonService.atpApplicationFilter.applicationDateFilterEnd = this.applicationDateFilterEnd.value === null ? null :  new Date(this.applicationDateFilterEnd.value);
      this.commonService.atpApplicationFilter.selectedRenewal = this.selectedRenewal;
    }    

    this.filteredApplications = this.atpApplicationTable.layout.data.filter(x=>(x.firmName + ' ' + x.id + ' ').toLowerCase().includes(this.filterString.trim().toLowerCase()));    
    if (this.selectedStatus.value.length > 0){
      this.filteredApplications = this.filteredApplications.filter(x=>this.selectedStatus.value.includes(x.applicationStatus));
    }  
    if (this.selectedType.value.length > 0){
      this.filteredApplications = this.filteredApplications.filter(x=>this.selectedType.value.includes(x.applicationType));
    } 
    if (this.showPendingLinkApprovals.value === false){
      this.filteredApplications = this.filteredApplications.filter(x => !this.isPendingLinkApproval(x));
    }
    if (this.applicationDateFilterStart.value){
      this.filteredApplications = this.filteredApplications.filter(x => {
        let applicationDate = new Date(x.applicationDate);
        applicationDate.setHours(0, 0, 0, 0);
    
        let filterStartDate = new Date(this.applicationDateFilterStart.value);
        filterStartDate.setHours(0, 0, 0, 0);
    
        return applicationDate >= filterStartDate;
      });      
    }
    if (this.applicationDateFilterEnd.value){
      this.filteredApplications = this.filteredApplications.filter(x => {
        let applicationDate = new Date(x.applicationDate);
        applicationDate.setHours(0, 0, 0, 0);
    
        let filterEndDate = new Date(this.applicationDateFilterEnd.value);
        filterEndDate.setHours(0, 0, 0, 0);
    
        return applicationDate <= filterEndDate;
    });      
    } 
    if (this.selectedRenewal.value !== '' && this.selectedRenewal.value !== null){
      if(this.selectedRenewal.value == 'Expired'){
        this.filteredApplications = this.filteredApplications.filter(x => {
          // Filter out applications with invalid/missing dates
          if (!x.applicationDate || x.applicationDate === '') {
            return false;
          }
          
          let currentDate = new Date();
          let applicationDate = new Date(x.applicationDate);
          let diffInTime = currentDate.getTime() - applicationDate.getTime();
          let diffInDays = diffInTime / (1000 * 3600 * 24);      
          return diffInDays > 90 && x.applicationStatus !== 'Approved' && x.applicationStatus !== 'Cancelled';
        });
      }      
      else if(this.selectedRenewal.value == 'Expiring'){        
        this.filteredApplications = this.filteredApplications.filter(x => {
          if (!x.applicationDate || x.applicationDate === '') {
            return false;
          }
          
          let currentDate = new Date();
          let applicationDate = new Date(x.applicationDate);
          let diffInTime = currentDate.getTime() - applicationDate.getTime();
          let diffInDays = diffInTime / (1000 * 3600 * 24);       
          return diffInDays >= 60 && diffInDays <= 90 && x.applicationStatus !== 'Approved' && x.applicationStatus !== 'Cancelled';
        });
      }
      else if(this.selectedRenewal.value == 'Outstanding'){        
        this.filteredApplications = this.filteredApplications.filter(x => {
          if (!x.applicationDate || x.applicationDate === '') {
            return false;
          }
          
          let currentDate = new Date();
          let applicationDate = new Date(x.applicationDate);
          let diffInTime = currentDate.getTime() - applicationDate.getTime();
          let diffInDays = diffInTime / (1000 * 3600 * 24);       
          return diffInDays > 30 && diffInDays <= 90 && x.applicationStatus !== 'Approved' && x.applicationStatus !== 'Cancelled';
        });
      }
      else if(this.selectedRenewal.value == 'Near Outstanding'){        
        this.filteredApplications = this.filteredApplications.filter(x => {
          if (!x.applicationDate || x.applicationDate === '') {
            return false;
          }
          
          let currentDate = new Date();
          let applicationDate = new Date(x.applicationDate);
          let diffInTime = currentDate.getTime() - applicationDate.getTime();
          let diffInDays = diffInTime / (1000 * 3600 * 24);       
          return diffInDays >= 20 && diffInDays <= 30 && x.applicationStatus !== 'Approved' && x.applicationStatus !== 'Cancelled';
        });
      }
    }   
    this.updateDataSource();  
  }

  isPendingLinkApproval(application: any): boolean {
    return this.isAccountLinkApplication(application) && this.pendingLinkApprovalApplicationIds.has(application.id);
  }

  clearFilters(){
    this.filterString = '';
    this.selectedStatus.setValue(['In Progress','Submitted','In Review','Action Needed']);
    this.selectedType.setValue([]);
    this.showPendingLinkApprovals.setValue(true);
    this.selectedRenewal.setValue('');
    this.applicationDateFilterStart.reset();
    this.applicationDateFilterEnd.reset();
    this.filterTable();
  }
}

